import { $FixMe } from '@eventbrite/ts-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { savedTags, toggleInterest } from '../../api/interests';

type Tag = { action_performed: boolean; tag: string; saved_by_you: boolean };
type TagsResponse = { tags?: Tag[] };

/**
 * @name useTagIsSaved
 * @description fetches the tags saved to a user's account and
 * returns a boolean value indicating whether the provided `tagId`
 * is contained therein
 * @param tagId {string}
 * @returns object containing boolean loading and saved states
 */
const useTagIsSaved = (tagId: string, isAuthenticated: boolean) => {
    const { data, isLoading } = useQuery(
        ['categoryBrowse', 'savedTags'],
        () => savedTags(),
        {
            enabled: isAuthenticated && tagId.includes('SubCategory'),
        },
    );
    let isSaved = false;

    if (!isLoading)
        isSaved = data
            ? data.tags.filter((t: Tag) => t.tag === tagId).length > 0
            : false;
    return { isLoading, isSaved };
};

/**
 * @name useToggleTag
 * @param tagId {string}
 * @param saved {boolean}
 * @returns object containing boolean representation of the query's loading
 * state, the saved state of the tag and a method to toggle the tag's save state
 */
const useToggleTag = (tagId: string) => {
    const queryClient = useQueryClient();
    //TODO: Verify this mutation operates as expected.
    return useMutation(
        ['categoryBrowse', 'toggleTag'],
        (saved: boolean) => toggleInterest(tagId, saved),
        {
            onSuccess: (tags: TagsResponse) => {
                queryClient.setQueryData<TagsResponse | undefined>(
                    ['categoryBrowse', 'savedTags'],
                    (oldData: $FixMe) => {
                        const shouldAddTag = tags.tags?.[0].saved_by_you;
                        if (shouldAddTag) {
                            return {
                                tags: [
                                    ...(oldData?.tags || []),
                                    tags.tags?.[0],
                                ],
                            };
                        } else {
                            return {
                                tags: oldData?.tags?.filter(
                                    (tag: { tag: string }) => {
                                        return tag.tag !== tagId;
                                    },
                                ),
                            };
                        }
                    },
                );
            },
        },
    );
};

/**
 * @name useSavedTags
 * @description interface exposed to consuming components that checks if the
 * user already has the provided tag saved and then allows the save state to
 * be toggled
 * @param tagId {string}
 * @returns object containing boolean representation of the query's loading
 * state, the saved state of the tag and a method to toggle the tag's save state
 */
export const useSavedTags = (tagId: string, isAuthenticated: boolean) => {
    const { isLoading, isSaved } = useTagIsSaved(tagId, isAuthenticated);
    const toggleTag = useToggleTag(tagId);
    const loading = isLoading || toggleTag.isLoading;

    return {
        loading,
        isSaved,
        toggleTag: (isSaved: boolean) => toggleTag.mutate(isSaved),
    };
};
